// ============================================================================
// ADR-0024 N-4 — structured grids of a note ("Additional information")
// ============================================================================
// Edits the values of every grid the note's component defines (Corporate
// Facts `note_grids`) — the Additional Information (a)–(r) tables, CSR and the
// charges not registered. The definitions come from the component; nothing
// here is typed as table text.
//
//   fixed — the component's rows; CY / PY amounts (or text rows). A derived row
//           shows its computed CY read-only (e.g. (c) proposed dividend from the
//           Corporate Facts dividends).
//   items — the auditor lists the major items; "Others" = the saved statement
//           total less the items, with the 10% guidance.
//   list  — free rows (charges not registered).
//
// Hosted by Corporate Facts (Additional information section) — the one place
// these figures are entered; a note opens it in a side sheet. Its save also
// writes Corporate Facts `forex` (earnings / outgo) from the rows flagged
// forex (forexFromGrids), so the Board's Report Annexure A reads the same figures.

import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { generateLocalId } from 'components/modals/modules/fsa/notes/panes/policy/SectionedNarrativeEditor';
import { formatStatementAmount, resolveVariant } from 'components/modals/modules/fsa/reports/reportSections';
import type { CorporateFactsData, NoteGridValues } from 'api/types/fsa/corporateFacts.types';
import type { RatioOpening, ReportSectionDefinition, ReportTable, SectionGrid, SectionGridRow } from 'api/types/fsa/reportData.types';


type Cell = number | string | null | undefined;

function parseAmount(v: string): number | null {
  const t = v.trim().replace(/,/g, '');
  if (!t) return null;
  const neg = /^\(.*\)$/.test(t);
  const n = Number(t.replace(/[()]/g, ''));
  return Number.isFinite(n) ? (neg ? -n : n) : null;
}

/** "5,41,36,74,860.60" / "(1,000.00)" → number (live field values are formatted). */
function parseFormatted(v: string | undefined): number | null {
  return v == null ? null : parseAmount(v);
}

function finalDividends(data: CorporateFactsData): number {
  return (data.dividends ?? []).filter(d => d.kind === 'FINAL').reduce((s, d) => s + (d.total_amount ?? 0), 0);
}

function derivedCy(row: SectionGridRow, data: CorporateFactsData): number | null | undefined {
  if (!row.source) return undefined;
  if (row.source === 'dividend_proposed') return finalDividends(data);
  let total: number | null = null;
  for (const part of row.source.sum) {
    const v = data.note_grids?.[part.grid]?.rows?.[part.row]?.cy;
    if (typeof v === 'number') total = (total ?? 0) + v;
  }
  return total;
}

/** Corporate Facts forex (Annexure A) from the grid rows flagged forex. Null when no forex row has a value. */
export function forexFromGrids(grids: SectionGrid[], data: CorporateFactsData): { earnings: number; outgo: number } | null {
  let earnings = 0;
  let outgo = 0;
  let any = false;
  for (const g of grids) {
    for (const r of g.rows ?? []) {
      if (!r.forex) continue;
      const v = data.note_grids?.[g.code]?.rows?.[r.code]?.cy;
      if (typeof v !== 'number') continue;
      any = true;
      if (r.forex === 'earnings') earnings += v; else outgo += v;
    }
  }
  return any ? { earnings, outgo } : null;
}

// ---------------------------------------------------------------------------

/**
 * The Additional Information grids — (a)–(r), CSR, charges, forex, ratio
 * inputs and reasons — as an inline editor over a Corporate Facts draft.
 * Hosted by Corporate Facts (its Additional information section) and by
 * the note's popup.
 */
export const NoteGridsEditor: React.FC<{
  defs: ReportSectionDefinition[];
  data: CorporateFactsData;
  update: (fn: (d: CorporateFactsData) => CorporateFactsData) => void;
  liveFields?: Record<string, string>;
  liveTables?: Record<string, ReportTable>;
  liveRatioOpenings?: RatioOpening[];
  hasPriorEngagement: boolean;
  refreshing: boolean;
  refreshBlockedReason: string | null;
  onRefreshOpenings: () => void;
}> = ({ defs, data, update, liveFields, liveTables, liveRatioOpenings, hasPriorEngagement, refreshing, refreshBlockedReason, onRefreshOpenings }) => {
  const sections = defs.filter(d => (d.grids ?? []).length > 0);
  const setGrid = (code: string, next: NoteGridValues) =>
    update(d => ({ ...d, note_grids: { ...(d.note_grids ?? {}), [code]: next } }));
  return (
    <>
      {sections.map(s => {
        const applies = resolveVariant(s, undefined, data).applies;
        return (
          <div key={s.code} style={sectionStyle}>
            <div style={sectionTitleStyle}>{s.title}</div>
            {!applies ? (
              <div style={mutedStyle}>Not applicable on the current answer — answer “{s.applicability?.prompt}” Yes to enter figures.</div>
            ) : (s.grids ?? []).map(g => (
              <div key={g.code} style={{ marginBottom: 12 }}>
                {g.title && <div style={gridTitleStyle}>{g.title}</div>}
                {g.hint && <div style={mutedStyle}>{g.hint}</div>}
                {g.kind === 'fixed' && <FixedGrid grid={g} data={data} onChange={v => setGrid(g.code, v)} />}
                {g.kind === 'items' && <ItemsGrid grid={g} data={data} liveFields={liveFields} onChange={v => setGrid(g.code, v)} />}
                {g.kind === 'list' && <ListGrid grid={g} data={data} onChange={v => setGrid(g.code, v)} />}
                {g.kind === 'ratios' && (
                  <RatiosGrid
                    grid={g} data={data} computed={liveTables?.['grid.' + g.code]} onChange={v => setGrid(g.code, v)}
                    openings={liveRatioOpenings ?? []}
                    onOpeningsChange={next => update(d => ({ ...d, ratio_py_openings: next }))}
                    hasPriorEngagement={hasPriorEngagement}
                    refreshing={refreshing}
                    refreshBlockedReason={refreshBlockedReason}
                    onRefresh={onRefreshOpenings}
                  />
                )}
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
};

const AmountInput: React.FC<{ value: Cell; onCommit: (v: number | null) => void; disabled?: boolean }> = ({ value, onCommit, disabled }) => {
  const shown = typeof value === 'number' ? String(value) : '';
  const [text, setText] = useState(shown);
  useEffect(() => { setText(shown); }, [shown]);
  return (
    <input className="form-input" style={amountStyle} value={text} disabled={disabled} inputMode="decimal"
      onChange={e => setText(e.target.value)}
      onBlur={() => { const n = parseAmount(text); if (n !== (typeof value === 'number' ? value : null)) onCommit(n); }} />
  );
};

const FixedGrid: React.FC<{ grid: SectionGrid; data: CorporateFactsData; onChange: (v: NoteGridValues) => void }> = ({ grid, data, onChange }) => {
  const values = data.note_grids?.[grid.code] ?? {};
  const rows = values.rows ?? {};
  const set = (code: string, key: 'cy' | 'py', v: Cell) => {
    const cell = { ...(rows[code] ?? {}), [key]: v };
    onChange({ ...values, rows: { ...rows, [code]: cell } });
  };
  return (
    <table style={tableStyle}>
      <thead><tr><th style={thStyle}>{grid.label_column ?? 'Particulars'}</th><th style={thNumStyle}>Current year</th><th style={thNumStyle}>Previous year</th></tr></thead>
      <tbody>
        {(grid.rows ?? []).map(r => {
          if (r.kind === 'heading') return <tr key={r.code}><td style={{ ...tdStyle, fontWeight: 700 }} colSpan={3}>{r.label}</td></tr>;
          const cell = rows[r.code] ?? {};
          const derived = derivedCy(r, data);
          return (
            <tr key={r.code}>
              <td style={{ ...tdStyle, paddingLeft: 8 + 16 * (r.indent ?? 0) }}>
                {r.label}
                {r.forex && <span style={tagStyle} title="Feeds Annexure A of the Board's Report">forex {r.forex}</span>}
              </td>
              {r.kind === 'text' ? (
                <>
                  <td style={tdStyle}><input className="form-input" value={typeof cell.cy === 'string' ? cell.cy : ''} onChange={e => set(r.code, 'cy', e.target.value || null)} /></td>
                  <td style={tdStyle}><input className="form-input" value={typeof cell.py === 'string' ? cell.py : ''} onChange={e => set(r.code, 'py', e.target.value || null)} /></td>
                </>
              ) : (
                <>
                  <td style={tdNumStyle}>
                    {derived !== undefined
                      ? <span title={r.source === 'dividend_proposed' ? 'Final dividends recorded in Corporate Facts' : 'Sum of the rows it is built from'}>
                          {derived == null ? '-' : formatStatementAmount(derived)} <em style={mutedInline}>auto</em>
                        </span>
                      : <AmountInput value={cell.cy} onCommit={v => set(r.code, 'cy', v)} />}
                  </td>
                  <td style={tdNumStyle}><AmountInput value={cell.py} onCommit={v => set(r.code, 'py', v)} /></td>
                </>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const ItemsGrid: React.FC<{
  grid: SectionGrid;
  data: CorporateFactsData;
  liveFields?: Record<string, string>;
  onChange: (v: NoteGridValues) => void;
}> = ({ grid, data, liveFields, onChange }) => {
  const values = data.note_grids?.[grid.code] ?? {};
  const items = values.items ?? [];
  const totalCy = grid.others_from ? parseFormatted(liveFields?.[`fs.${grid.others_from}.cy`]) : null;
  const totalPy = grid.others_from ? parseFormatted(liveFields?.[`fs.${grid.others_from}.py`]) : null;
  const sumCy = items.reduce((s, i) => s + (i.cy ?? 0), 0);
  const sumPy = items.reduce((s, i) => s + (i.py ?? 0), 0);
  const othersCy = totalCy == null ? null : Math.abs(totalCy) - sumCy;
  const othersPy = totalPy == null ? null : Math.abs(totalPy) - sumPy;
  const tenPct = totalCy ? Math.abs(totalCy) * 0.1 : null;
  const setItems = (next: typeof items) => onChange({ ...values, items: next });
  const update = (id: string, patch: Partial<(typeof items)[number]>) => setItems(items.map(i => (i.local_id === id ? { ...i, ...patch } : i)));

  return (
    <>
      <table style={tableStyle}>
        <thead><tr><th style={thStyle}>Item</th><th style={thNumStyle}>Current year</th><th style={thNumStyle}>Previous year</th><th style={thStyle} /></tr></thead>
        <tbody>
          {items.map(i => (
            <tr key={i.local_id}>
              <td style={tdStyle}>
                <input className="form-input" value={i.name} placeholder="Item" onChange={e => update(i.local_id, { name: e.target.value })} />
                {tenPct != null && i.cy != null && Math.abs(i.cy) < tenPct && (
                  <div style={warnStyle}>Below 10% of the total — may be left in “Others”.</div>
                )}
              </td>
              <td style={tdNumStyle}><AmountInput value={i.cy} onCommit={v => update(i.local_id, { cy: v })} /></td>
              <td style={tdNumStyle}><AmountInput value={i.py} onCommit={v => update(i.local_id, { py: v })} /></td>
              <td style={tdStyle}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setItems(items.filter(x => x.local_id !== i.local_id))} title="Remove">
                  <IconSystem.trash size={12} />
                </button>
              </td>
            </tr>
          ))}
          {grid.others_from && (
            <tr>
              <td style={{ ...tdStyle, fontStyle: 'italic' }}>
                {grid.others_label ?? 'Others'} <span style={mutedInline}>(statement total less the items)</span>
                {tenPct != null && othersCy != null && othersCy > tenPct && items.length > 0 && (
                  <div style={warnStyle}>“Others” is more than 10% of the total — list any item above 10%.</div>
                )}
              </td>
              <td style={tdNumStyle}>{othersCy == null ? <em style={mutedInline}>save the statement</em> : formatStatementAmount(othersCy)}</td>
              <td style={tdNumStyle}>{othersPy == null ? '' : formatStatementAmount(othersPy)}</td>
              <td />
            </tr>
          )}
        </tbody>
      </table>
      <button type="button" style={linkBtnStyle} onClick={() => setItems([...items, { local_id: generateLocalId(), name: '' }])}>+ add item</button>
    </>
  );
};

const ListGrid: React.FC<{ grid: SectionGrid; data: CorporateFactsData; onChange: (v: NoteGridValues) => void }> = ({ grid, data, onChange }) => {
  const values = data.note_grids?.[grid.code] ?? {};
  const entries = values.entries ?? [];
  const cols = grid.columns ?? [];
  const setEntries = (next: typeof entries) => onChange({ ...values, entries: next });
  return (
    <>
      <table style={tableStyle}>
        <thead><tr>{cols.map(c => <th key={c.key} style={thStyle}>{c.label}</th>)}<th style={thStyle} /></tr></thead>
        <tbody>
          {entries.length === 0 && <tr><td style={{ ...tdStyle, ...mutedStyle }} colSpan={cols.length + 1}>No rows — prints “{grid.empty_text ?? 'Nil'}”.</td></tr>}
          {entries.map(e => (
            <tr key={e.local_id}>
              {cols.map(c => (
                <td key={c.key} style={tdStyle}>
                  <input className="form-input" value={e[c.key] ?? ''}
                    onChange={ev => setEntries(entries.map(x => (x.local_id === e.local_id ? { ...x, [c.key]: ev.target.value } : x)))} />
                </td>
              ))}
              <td style={tdStyle}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEntries(entries.filter(x => x.local_id !== e.local_id))} title="Remove">
                  <IconSystem.trash size={12} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" style={linkBtnStyle} onClick={() => setEntries([...entries, { local_id: generateLocalId() }])}>+ add row</button>
    </>
  );
};

/**
 * ADR-0024 N-5 — ratios: the formulas run on the server from the saved
 * statements; here the auditor enters the inputs the statements do not hold,
 * may override a numerator / denominator, and gives the reason for any change
 * above 25%. The values shown are as of the last save.
 */
const DEFAULT_RATIO_FOOTNOTE = '* Previous year ratio computed on closing balances.';

type StoredOpenings = NonNullable<CorporateFactsData['ratio_py_openings']>;

/**
 * ADR-0024 N-5 — the previous year's opening balances the ratios average,
 * STORED in this engagement: taken from the previous year's saved Balance Sheet
 * (at Create Next Year, or "Refresh from previous year") or typed here for a
 * first year in PRIMA. Typing over a taken value marks it as entered.
 */
const RatioOpeningsTable: React.FC<{
  openings: RatioOpening[];
  stored: StoredOpenings;
  onChange: (next: StoredOpenings) => void;
  hasPriorEngagement: boolean;
  refreshing: boolean;
  refreshBlockedReason: string | null;
  onRefresh: () => void;
}> = ({ openings, stored, onChange, hasPriorEngagement, refreshing, refreshBlockedReason, onRefresh }) => {
  if (openings.length === 0) return null;
  const set = (line: string, v: number | null) => {
    const next = { ...stored };
    if (v == null) delete next[line]; else next[line] = { amount: v, source: 'manual' };
    onChange(next);
  };
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <strong style={{ fontSize: '0.78rem' }}>Opening balances of the previous year</strong>
        <span style={mutedInline}>(for the ratios on average balances)</span>
        {hasPriorEngagement && (
          <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}
            disabled={refreshing || refreshBlockedReason != null}
            title={refreshBlockedReason ?? 'Take every opening balance from the previous year\'s saved Balance Sheet (replaces the values below).'}
            onClick={onRefresh}>
            <IconSystem.refresh size={13} /> {refreshing ? 'Refreshing…' : 'Refresh from previous year'}
          </button>
        )}
      </div>
      <table style={{ ...tableStyle, marginTop: 4 }}>
        <thead><tr><th style={thStyle}>Balance</th><th style={thNumStyle}>Opening (previous year)</th><th style={thStyle}>Source</th></tr></thead>
        <tbody>
          {openings.map(o => {
            const s = stored[o.line];
            const amount = typeof s?.amount === 'number' ? s.amount : null;
            const changedInPrior = s?.source === 'prior' && amount != null && o.priorAmount != null
              && Math.abs(o.priorAmount - amount) >= 0.005;
            return (
              <tr key={o.line}>
                <td style={tdStyle}>{o.label}</td>
                <td style={tdNumStyle}><AmountInput value={amount} onCommit={v => set(o.line, v)} /></td>
                <td style={tdStyle}>
                  {amount == null
                    ? <span style={warnStyle}>not entered — the previous-year ratio uses the closing balance</span>
                    : s?.source === 'prior'
                      ? <span style={mutedInline}>from the previous year</span>
                      : <span style={mutedInline}>entered</span>}
                  {changedInPrior && (
                    <div style={warnStyle}>The previous year now shows {formatStatementAmount(o.priorAmount!)} — refresh to update.</div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const RatiosGrid: React.FC<{
  grid: SectionGrid;
  data: CorporateFactsData;
  computed?: ReportTable;
  onChange: (v: NoteGridValues) => void;
  openings: RatioOpening[];
  onOpeningsChange: (next: StoredOpenings) => void;
  hasPriorEngagement: boolean;
  refreshing: boolean;
  refreshBlockedReason: string | null;
  onRefresh: () => void;
}> = ({ grid, data, computed, onChange, openings, onOpeningsChange, hasPriorEngagement, refreshing, refreshBlockedReason, onRefresh }) => {
  const values = data.note_grids?.[grid.code] ?? {};
  const inputs = values.inputs ?? {};
  const overrides = values.overrides ?? {};
  const explanations = values.explanations ?? {};
  const [openOverride, setOpenOverride] = useState<string | null>(null);
  const setInput = (code: string, key: 'cy' | 'py', v: number | null) =>
    onChange({ ...values, inputs: { ...inputs, [code]: { ...(inputs[code] ?? {}), [key]: v } } });
  const setOverride = (code: string, key: 'num_cy' | 'num_py' | 'den_cy' | 'den_py', v: number | null) =>
    onChange({ ...values, overrides: { ...overrides, [code]: { ...(overrides[code] ?? {}), [key]: v } } });
  const setExplanation = (code: string, text: string) => {
    const next = { ...explanations };
    if (text.trim()) next[code] = text; else delete next[code];
    onChange({ ...values, explanations: next });
  };

  return (
    <>
      <RatioOpeningsTable
        openings={openings} stored={data.ratio_py_openings ?? {}} onChange={onOpeningsChange}
        hasPriorEngagement={hasPriorEngagement} refreshing={refreshing}
        refreshBlockedReason={refreshBlockedReason} onRefresh={onRefresh}
      />
      {(grid.inputs ?? []).length > 0 && (
        <table style={{ ...tableStyle, marginBottom: 8 }}>
          <thead><tr><th style={thStyle}>Input</th><th style={thNumStyle}>Current year</th><th style={thNumStyle}>Previous year</th></tr></thead>
          <tbody>
            {(grid.inputs ?? []).map(i => (
              <tr key={i.code}>
                <td style={tdStyle}>{i.label}{i.hint && <div style={mutedStyle}>{i.hint}</div>}</td>
                <td style={tdNumStyle}><AmountInput value={inputs[i.code]?.cy} onCommit={v => setInput(i.code, 'cy', v)} /></td>
                <td style={tdNumStyle}><AmountInput value={inputs[i.code]?.py} onCommit={v => setInput(i.code, 'py', v)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.76rem', margin: '4px 0', color: 'var(--text-primary)' }}>
        <input type="checkbox" checked={values.hide_reason === true}
          onChange={e => onChange({ ...values, hide_reason: e.target.checked || undefined })} />
        Print without the “Reason for variance” column
        <span style={mutedInline}>(no “reason required” checklist points either)</span>
      </label>
      <div style={mutedStyle}>Computed from the saved statements as of the last save — Save to recompute after changing inputs or overrides.</div>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Ratio</th><th style={thNumStyle}>Current year</th><th style={thNumStyle}>Previous year</th>
            <th style={thNumStyle}>% Variance</th><th style={thStyle}>Reason for variance</th>
          </tr>
        </thead>
        <tbody>
          {(grid.ratios ?? []).map((r, i) => {
            const row = computed?.rows?.[i];
            const variance = row ? parseFloat(row[5]) : NaN;
            const needsReason = Number.isFinite(variance) && Math.abs(variance) > 25;
            const ov = overrides[r.code] ?? {};
            const overridden = Object.values(ov).some(v => typeof v === 'number');
            return (
              <React.Fragment key={r.code}>
                <tr>
                  <td style={tdStyle}>
                    {r.label} <span style={mutedInline}>({r.unit === '%' ? '%' : 'times'})</span>
                    <div style={mutedStyle}>{r.numerator_label} ÷ {r.denominator_label}</div>
                    <button type="button" style={linkBtnStyle} onClick={() => setOpenOverride(o => (o === r.code ? null : r.code))}>
                      {overridden ? 'Overridden — edit' : 'Override numerator / denominator'}
                    </button>
                  </td>
                  <td style={tdNumStyle}>{row?.[3] ?? '-'}</td>
                  <td style={tdNumStyle}>{row?.[4] ?? '-'}</td>
                  <td style={{ ...tdNumStyle, color: needsReason ? '#b45309' : undefined, fontWeight: needsReason ? 700 : undefined }}>{row?.[5] ?? '-'}</td>
                  <td style={tdStyle}>
                    {needsReason || explanations[r.code] ? (
                      <textarea className="form-textarea" rows={2} style={{ width: '100%', fontSize: '0.76rem' }}
                        value={explanations[r.code] ?? ''} placeholder="Reason for the change (required above 25%)"
                        onChange={e => setExplanation(r.code, e.target.value)} />
                    ) : <span style={mutedInline}>not required</span>}
                  </td>
                </tr>
                {openOverride === r.code && (
                  <tr>
                    <td style={tdStyle} colSpan={5}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, auto)', gap: 8, alignItems: 'center', fontSize: '0.74rem' }}>
                        <span>Numerator — CY</span><AmountInput value={ov.num_cy} onCommit={v => setOverride(r.code, 'num_cy', v)} />
                        <span>Numerator — PY</span><AmountInput value={ov.num_py} onCommit={v => setOverride(r.code, 'num_py', v)} />
                        <span>Denominator — CY</span><AmountInput value={ov.den_cy} onCommit={v => setOverride(r.code, 'den_cy', v)} />
                        <span>Denominator — PY</span><AmountInput value={ov.den_py} onCommit={v => setOverride(r.code, 'den_py', v)} />
                      </div>
                      <div style={mutedStyle}>Leave blank to use the figure computed from the statements.</div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      <label style={{ display: 'block', fontSize: '0.76rem', marginTop: 8, color: 'var(--text-primary)' }}>
        Footnote <span style={mutedInline}>— printed only when a previous-year ratio uses closing balances (marked *); clear it to print none</span>
        <textarea className="form-textarea" rows={2} style={{ width: '100%', fontSize: '0.76rem' }}
          value={values.footnote ?? DEFAULT_RATIO_FOOTNOTE}
          onChange={e => onChange({ ...values, footnote: e.target.value === DEFAULT_RATIO_FOOTNOTE ? undefined : e.target.value })} />
      </label>
      {(computed?.warnings ?? []).length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {computed!.warnings!.map(w => <li key={w} style={warnStyle}>{w}</li>)}
        </ul>
      )}
    </>
  );
};

const sectionStyle: React.CSSProperties = { background: 'var(--bg-secondary)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '0.75rem' };
const sectionTitleStyle: React.CSSProperties = { fontWeight: 700, fontSize: '0.9rem', marginBottom: 6, color: 'var(--text-primary)' };
const gridTitleStyle: React.CSSProperties = { fontWeight: 600, fontSize: '0.8rem', margin: '4px 0', color: 'var(--text-primary)' };
const mutedStyle: React.CSSProperties = { fontSize: '0.74rem', color: 'var(--text-secondary)', marginBottom: 4 };
const mutedInline: React.CSSProperties = { fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 400 };
const warnStyle: React.CSSProperties = { fontSize: '0.7rem', color: '#92400e' };
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' };
const thStyle: React.CSSProperties = { textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' };
const thNumStyle: React.CSSProperties = { ...thStyle, textAlign: 'right', width: 170 };
const tdStyle: React.CSSProperties = { padding: '3px 6px', borderBottom: '1px solid var(--border-color)', verticalAlign: 'top', color: 'var(--text-primary)' };
const tdNumStyle: React.CSSProperties = { ...tdStyle, textAlign: 'right' };
const amountStyle: React.CSSProperties = { width: 150, textAlign: 'right', padding: '2px 6px', fontSize: '0.78rem' };
const tagStyle: React.CSSProperties = { marginLeft: 6, padding: '0 5px', borderRadius: 999, background: '#e0f2fe', color: '#075985', fontSize: '0.62rem', fontWeight: 700 };
const linkBtnStyle: React.CSSProperties = {
  border: 'none', background: 'transparent', color: 'var(--primary-color, #2563eb)', cursor: 'pointer', fontSize: '0.75rem', padding: '4px 0',
};

